import React, { useState, useEffect } from "react";
import EntryList from "../journal/EntryList";
import EntryForm from "../journal/EntryForm";
import EntryDetail from "../journal/EntryDetail";
import ConfirmDialog from "../common/ConfirmDialog";
import { Search as SearchIcon, X, SlidersHorizontal } from "lucide-react";
import {
  fetchJournalEntries,
  createJournalEntry,
  updateJournalEntry,
  deleteJournalEntry,
} from "../../api/api";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLanguage } from "../../i18n/LanguageContext";

const PAGE_SIZE = 10;

const splitTags = (value) => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (!value) return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const JournalPage = () => {
  const { t } = useLanguage();
  const sentimentOptions = [
    { value: "", label: t("filters.all") },
    { value: "1", label: t("mood.terrible") },
    { value: "2", label: t("mood.bad") },
    { value: "3", label: t("mood.okay") },
    { value: "4", label: t("mood.good") },
    { value: "5", label: t("mood.great") },
  ];
  const sleepOptions = [
    { value: "", label: t("filters.all") },
    { value: "1", label: t("sleep.veryPoor") },
    { value: "2", label: t("sleep.poor") },
    { value: "3", label: t("sleep.fair") },
    { value: "4", label: t("sleep.good") },
    { value: "5", label: t("sleep.excellent") },
  ];
  const stressOptions = [
    { value: "", label: t("filters.all") },
    { value: "1", label: t("stress.veryLow") },
    { value: "2", label: t("stress.low") },
    { value: "3", label: t("stress.moderate") },
    { value: "4", label: t("stress.high") },
    { value: "5", label: t("stress.veryHigh") },
  ];
  const socialEngagementOptions = [
    { value: "", label: t("filters.all") },
    { value: "1", label: t("social.veryIsolated") },
    { value: "2", label: t("social.isolated") },
    { value: "3", label: t("social.neutral") },
    { value: "4", label: t("social.social") },
    { value: "5", label: t("social.verySocial") },
  ];
  const [showEntryFormModal, setShowEntryFormModal] = useState(false);
  const [showEntryDetailModal, setShowEntryDetailModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sentimentFilter, setSentimentFilter] = useState("");
  const [activityFilter, setActivityFilter] = useState("");
  const [sleepFilter, setSleepFilter] = useState("");
  const [stressFilter, setStressFilter] = useState("");
  const [socialEngagementFilter, setSocialEngagementFilter] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState(null);
  const [entryPendingDelete, setEntryPendingDelete] = useState(null);

  const queryClient = useQueryClient();

  const {
    data: allEntries,
    isLoading,
    isError,
    error: fetchError,
  } = useQuery({
    queryKey: ["journalEntries"],
    queryFn: fetchJournalEntries,
    staleTime: 5 * 60 * 1000,
  });

  const [filteredEntries, setFilteredEntries] = useState([]);

  const activityOptions = Array.from(
    new Set((allEntries || []).flatMap((entry) => splitTags(entry.activities)))
  ).sort((a, b) => a.localeCompare(b));

  useEffect(() => {
    if (!allEntries) {
      setFilteredEntries([]);
      return;
    }

    let updatedFilteredEntries = allEntries;

    if (searchTerm) {
      const normalizedSearch = searchTerm.toLowerCase();
      updatedFilteredEntries = updatedFilteredEntries.filter((entry) => {
        const haystack = `${entry.title || ""} ${entry.content || ""}`.toLowerCase();
        return haystack.includes(normalizedSearch);
      });
    }

    if (activityFilter) {
      updatedFilteredEntries = updatedFilteredEntries.filter((entry) =>
        splitTags(entry.activities).some(
          (activity) => activity.toLowerCase() === activityFilter.toLowerCase()
        )
      );
    }

    if (sentimentFilter) {
      updatedFilteredEntries = updatedFilteredEntries.filter(
        (entry) =>
          entry.sentiment_level !== null &&
          entry.sentiment_level !== undefined &&
          String(entry.sentiment_level) === sentimentFilter
      );
    }

    if (sleepFilter) {
      updatedFilteredEntries = updatedFilteredEntries.filter(
        (entry) =>
          entry.sleep_quality !== null &&
          entry.sleep_quality !== undefined &&
          String(entry.sleep_quality) === sleepFilter
      );
    }

    if (stressFilter) {
      updatedFilteredEntries = updatedFilteredEntries.filter(
        (entry) =>
          entry.stress_level !== null &&
          entry.stress_level !== undefined &&
          String(entry.stress_level) === stressFilter
      );
    }

    if (socialEngagementFilter) {
      updatedFilteredEntries = updatedFilteredEntries.filter(
        (entry) =>
          entry.social_engagement !== null &&
          entry.social_engagement !== undefined &&
          String(entry.social_engagement) === socialEngagementFilter
      );
    }

    if (startDate || endDate) {
      updatedFilteredEntries = updatedFilteredEntries.filter((entry) => {
        const entryDate = new Date(entry.date);
        let isWithinRange = true;

        if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          if (entryDate < start) {
            isWithinRange = false;
          }
        }

        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          if (entryDate > end) {
            isWithinRange = false;
          }
        }

        return isWithinRange;
      });
    }

    const sortedFilteredEntries = updatedFilteredEntries.sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
    setFilteredEntries(sortedFilteredEntries);
  }, [
    searchTerm,
    activityFilter,
    startDate,
    endDate,
    sentimentFilter,
    sleepFilter,
    stressFilter,
    socialEngagementFilter,
    allEntries,
  ]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [
    searchTerm,
    activityFilter,
    startDate,
    endDate,
    sentimentFilter,
    sleepFilter,
    stressFilter,
    socialEngagementFilter,
  ]);

  const visibleEntries = filteredEntries.slice(0, visibleCount);

  const createEntryMutation = useMutation({
    mutationFn: createJournalEntry,
    onMutate: () => {
      closeFormModal();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["journalEntries"] });
    },
    onError: (err) => {
      console.error("Error creating entry:", err);
      setError(t("journal.createError", { message: err.message }));
    },
  });

  const updateEntryMutation = useMutation({
    mutationFn: ({ entryId, entryData }) =>
      updateJournalEntry(entryId, entryData),
    onMutate: () => {
      closeFormModal();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["journalEntries"] });
    },
    onError: (err) => {
      console.error("Error updating entry:", err);
      setError(t("journal.updateError", { message: err.message }));
    },
  });

  const deleteEntryMutation = useMutation({
    mutationFn: deleteJournalEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["journalEntries"] });
      setEntryPendingDelete(null);
      if (showEntryDetailModal) {
        closeDetailModal();
      }
    },
    onError: (err) => {
      console.error("Error deleting entry:", err);
      setError(t("journal.deleteError", { message: err.message }));
    },
  });

  const handleSaveEntry = async (entryToSave) => {
    setError(null);
    const entryData = {
      content: entryToSave.content,
      sentiment_level: entryToSave.sentiment,
      sleep_quality: entryToSave.sleep,
      stress_level: entryToSave.stress,
      social_engagement: entryToSave.socialEngagement,
      date: entryToSave.date,
      title: entryToSave.title,
    };

    if (entryToSave.id) {
      updateEntryMutation.mutate({ entryId: entryToSave.id, entryData });
    } else {
      createEntryMutation.mutate(entryData);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    setEntryPendingDelete(entryId);
  };

  const confirmDeleteEntry = () => {
    if (!entryPendingDelete) {
      return;
    }
    setError(null);
    deleteEntryMutation.mutate(entryPendingDelete);
  };

  const openAddEntryModal = () => {
    setEditingEntry(null);
    setShowEntryFormModal(true);
  };

  const openEditEntryModal = (entry) => {
    setEditingEntry(entry);
    setShowEntryFormModal(true);
  };

  const handleSelectEntry = (entry) => {
    setSelectedEntry(entry);
    setShowEntryDetailModal(true);
  };

  const closeFormModal = () => {
    setShowEntryFormModal(false);
    setEditingEntry(null);
    setError(null);
  };

  const closeDetailModal = () => {
    setShowEntryDetailModal(false);
    setSelectedEntry(null);
  };

  const handleEditFromDetail = (entry) => {
    closeDetailModal();
    openEditEntryModal(entry);
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("journal.search")}
            className="input-field pl-9 py-2 text-sm"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`p-2.5 rounded-xl transition-colors ${
            showFilters ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
          }`}
        >
          <SlidersHorizontal className="h-4 w-4" />
        </button>
        <button onClick={openAddEntryModal} className="btn-primary ml-auto">
          {t("journal.newEntry")}
        </button>
      </div>

      {showFilters && (
        <div className="mb-6 flex flex-wrap gap-3 items-center">
          <select
            value={sentimentFilter}
            onChange={(e) => setSentimentFilter(e.target.value)}
            className="text-sm rounded-lg bg-gray-100 border-0 px-3 py-2 text-gray-600 focus:ring-2 focus:ring-purple-500"
          >
            {sentimentOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.value === "" ? t("journal.mood") : option.label}
              </option>
            ))}
          </select>
          <select
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
            className="text-sm rounded-lg bg-gray-100 border-0 px-3 py-2 text-gray-600 focus:ring-2 focus:ring-purple-500"
          >
            <option value="">{t("journal.activities")}</option>
            {activityOptions.map((activity) => (
              <option key={activity} value={activity}>
                {activity}
              </option>
            ))}
          </select>
          <select
            value={sleepFilter}
            onChange={(e) => setSleepFilter(e.target.value)}
            className="text-sm rounded-lg bg-gray-100 border-0 px-3 py-2 text-gray-600 focus:ring-2 focus:ring-purple-500"
          >
            {sleepOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.value === "" ? t("journal.sleep") : option.label}
              </option>
            ))}
          </select>
          <select
            value={stressFilter}
            onChange={(e) => setStressFilter(e.target.value)}
            className="text-sm rounded-lg bg-gray-100 border-0 px-3 py-2 text-gray-600 focus:ring-2 focus:ring-purple-500"
          >
            {stressOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.value === "" ? t("journal.stress") : option.label}
              </option>
            ))}
          </select>
          <select
            value={socialEngagementFilter}
            onChange={(e) => setSocialEngagementFilter(e.target.value)}
            className="text-sm rounded-lg bg-gray-100 border-0 px-3 py-2 text-gray-600 focus:ring-2 focus:ring-purple-500"
          >
            {socialEngagementOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.value === "" ? t("journal.social") : option.label}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="text-sm rounded-lg bg-gray-100 border-0 px-3 py-2 text-gray-600 focus:ring-2 focus:ring-purple-500"
            placeholder="From"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="text-sm rounded-lg bg-gray-100 border-0 px-3 py-2 text-gray-600 focus:ring-2 focus:ring-purple-500"
            placeholder="To"
          />
        </div>
      )}

      {isLoading && (
        <p className="text-center text-gray-500">{t("journal.loading")}</p>
      )}
      {(isError || error) && (
        <p className="text-center text-red-500">
          {t("common.error")}: {error || fetchError?.message}
        </p>
      )}

      {!isLoading && !isError && allEntries && (
        <>
          <EntryList
            entries={visibleEntries}
            onEditEntry={openEditEntryModal}
            onDeleteEntry={handleDeleteEntry}
            onSelectEntry={handleSelectEntry}
          />
          {visibleCount < filteredEntries.length && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                className="rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
              >
                {t("journal.loadMore")}
              </button>
            </div>
          )}
        </>
      )}

      {showEntryFormModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
          <div className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all w-full max-w-sm sm:max-w-md md:max-w-3xl lg:max-w-4xl xl:max-w-5xl p-6 max-h-[95vh] flex flex-col">
            <div className="absolute top-4 right-4">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                onClick={closeFormModal}
              >
                <span className="sr-only">{t("common.close")}</span>
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="pb-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-2xl font-bold leading-6 text-gray-900 break-words">
                {editingEntry ? t("journal.editEntry") : t("journal.newEntry")}
              </h3>
            </div>
            <EntryForm
              onClose={closeFormModal}
              onSave={handleSaveEntry}
              editEntry={editingEntry}
              isSaving={createEntryMutation.isPending || updateEntryMutation.isPending}
            />
          </div>
        </div>
      )}

      {showEntryDetailModal && (
        <EntryDetail
          entry={selectedEntry}
          onClose={closeDetailModal}
          onEdit={handleEditFromDetail}
          onDelete={handleDeleteEntry}
        />
      )}

      {entryPendingDelete && (
        <ConfirmDialog
          title={t("journal.deleteTitle")}
          message={t("journal.deleteMessage")}
          confirmLabel={t("journal.deleteConfirm")}
          isConfirming={deleteEntryMutation.isPending}
          onCancel={() => setEntryPendingDelete(null)}
          onConfirm={confirmDeleteEntry}
        />
      )}
    </div>
  );
};

export default JournalPage;
